import { z } from 'zod';
import { type Rarity, raritySchema } from '../rarity';
import { openWorldCommandIdSchema } from './crossing';

/**
 * **O comerciante de pesca: um em cada porto seguro, e em mais lado nenhum.**
 *
 * ## Onde ele está
 *
 * A lista dos oito portos **não é copiada para aqui**. Quem sabe se um
 * quadrante é um porto é `isOpenWorldSafePort`, e a sala já o pergunta uma vez
 * no construtor. Este ficheiro sabe apenas onde, *dentro* de um porto, o
 * comerciante fica — e essa posição é **derivada** e não guardada: um quadrante
 * é um quadrado de lado conhecido, e o centro dele é o centro dele.
 *
 * ## Duas economias, e a diferença importa
 *
 * - **Carga de campo** — os peixes que ainda estão no porão da nave. Vendê-los
 *   credita **Ouro de campo**: ele cai na morte, entra na caixa, e só se torna
 *   Ouro quando uma extração o comprometer. O livro-razão não o vê.
 * - **Coleção segura** — as capturas que já foram extraídas para a conta.
 *   Vendê-las credita **Ouro seguro**, pelo livro-razão canónico, com razão
 *   própria.
 *
 * Uma ordem nunca mistura as duas. Se misturasse, um jogador não teria como
 * saber que metade do que recebeu ainda podia perder ao morrer.
 *
 * ## O cliente não decide nada
 *
 * Ele não diz onde está, não diz a distância, não envia preço, raridade, peso
 * nem multiplicador, e não envia quantidade num "vender todos" — nesse caso ele
 * não envia lista nenhuma, e é o servidor que determina outra vez quais as
 * linhas elegíveis. O que ele envia é uma intenção e, no máximo, identificadores
 * de linhas que o servidor relê.
 */

/* -------------------------------------------------------------------------- */
/*  Onde ele fica                                                             */
/* -------------------------------------------------------------------------- */

/**
 * **A que distância se pode negociar**, em milésimos do lado do quadrante.
 *
 * Relativo ao lado e não em milli-unidades soltas: se o quadrante mudar de
 * tamanho, o alcance acompanha-o em vez de se tornar um ponto ou o quadrante
 * inteiro. Provisório — vinte e cinco milésimos são cerca de dois mil e
 * quarenta unidades de mundo, uns segundos de voo.
 */
export const OPEN_WORLD_MERCHANT_REACH_PERMILLE = 25;

/**
 * **Onde o comerciante está**, derivado do lado do quadrante.
 *
 * O centro. Determinístico, estável, e sem nada para persistir: dois portos com
 * o mesmo lado põem o comerciante no mesmo sítio, e reconstruir a sala não o
 * move. Um porto não tem geometria interna nenhuma — o catálogo devolve zero
 * espaços para ele — portanto não há nada com que ele possa colidir.
 */
export function openWorldMerchantPosition(sideMu: number): {
  readonly x: number;
  readonly y: number;
} {
  const centre = Math.trunc(sideMu / 2);
  return { x: centre, y: centre };
}

/** O alcance de negociação, em milli-unidades, para este lado de quadrante. */
export function openWorldMerchantReachMu(sideMu: number): number {
  return Math.max(1, Math.trunc((sideMu * OPEN_WORLD_MERCHANT_REACH_PERMILLE) / 1_000));
}

/* -------------------------------------------------------------------------- */
/*  As duas economias                                                         */
/* -------------------------------------------------------------------------- */

/**
 * De que lado uma venda acontece.
 *
 * `field` — o porão da nave, e o Ouro que ela paga é perigoso.
 * `vault` — a coleção da conta, e o Ouro que ela paga é seguro.
 */
export const OPEN_WORLD_SALE_SCOPES = ['field', 'vault'] as const;

export type OpenWorldSaleScope = (typeof OPEN_WORLD_SALE_SCOPES)[number];

/** Para onde o Ouro de uma venda foi. Dito no recibo, sem ambiguidade. */
export const OPEN_WORLD_SALE_DESTINATIONS = ['field_wallet', 'safe_wallet'] as const;

export type OpenWorldSaleDestination = (typeof OPEN_WORLD_SALE_DESTINATIONS)[number];

/** Porque é que uma ordem de venda foi recusada. Uma lista fechada. */
export const OPEN_WORLD_SALE_REFUSALS = [
  /** Não é um porto seguro. */
  'no_merchant',
  /** Longe do comerciante. */
  'too_far',
  /** A atravessar, a extrair, a canalizar, ou morto. */
  'busy',
  /** Nenhuma das linhas pedidas ainda é vendável. */
  'nothing_to_sell',
  /** O crédito faria a carteira passar o teto. Nada foi vendido. */
  'overflow',
  /** A autoridade não respondeu. Repetir a mesma ordem é seguro. */
  'unavailable',
] as const;

export type OpenWorldSaleRefusal = (typeof OPEN_WORLD_SALE_REFUSALS)[number];

/**
 * Quantas linhas uma ordem individual pode nomear.
 *
 * Um teto **explícito**, e não um corte silencioso: uma ordem maior é recusada
 * pelo esquema em vez de ser truncada, porque truncar seria vender menos do que
 * o jogador pediu e não lhe dizer.
 */
export const OPEN_WORLD_SALE_MAX_LINES = 64;

/* -------------------------------------------------------------------------- */
/*  As ordens do cliente                                                      */
/* -------------------------------------------------------------------------- */

/** Abrir o painel: pede as duas listas. Não decide nada, e repetir é o mesmo. */
export const openWorldMerchantOpenSchema = z
  .object({
    t: z.literal('mo'),
  })
  .strict();

export type OpenWorldMerchantOpen = z.infer<typeof openWorldMerchantOpenSchema>;

/**
 * **Vender.**
 *
 * `ids` nomeia linhas para uma venda individual — o servidor relê cada uma e
 * calcula o preço. `ids: null` é o **VENDER TODOS**: o cliente não envia lista
 * nenhuma, e é a autoridade que determina outra vez quais as elegíveis daquela
 * secção.
 *
 * Note o que não está aqui: preço, raridade, peso, multiplicador e quantidade.
 * O navegador envia uma intenção, e o servidor relê a realidade.
 */
export const openWorldMerchantSellSchema = z
  .object({
    t: z.literal('ms'),
    id: openWorldCommandIdSchema,
    sc: z.enum(OPEN_WORLD_SALE_SCOPES),
    ids: z.array(z.string().min(1).max(64)).min(1).max(OPEN_WORLD_SALE_MAX_LINES).nullable(),
  })
  .strict();

export type OpenWorldMerchantSell = z.infer<typeof openWorldMerchantSellSchema>;

/* -------------------------------------------------------------------------- */
/*  O que o comerciante mostra                                                */
/* -------------------------------------------------------------------------- */

/** Uma linha vendável, com o preço que a autoridade calculou. */
export const openWorldSaleLineSchema = z
  .object({
    /** O id da linha: do item de campo, ou da captura. */
    i: z.string().min(1).max(64),
    /** O id da espécie. O cliente resolve o nome pelo catálogo. */
    s: z.string().min(1).max(64),
    r: raritySchema,
    /** Miligramas inteiros. */
    w: z.number().int().min(1),
    /** O preço em Ouro, como decimal. A MESMA conta que a liquidação faz. */
    p: z.string().min(1).max(40),
  })
  .strict();

export type OpenWorldSaleLine = z.infer<typeof openWorldSaleLineSchema>;

/**
 * **O balcão**, dito só a quem está diante dele.
 *
 * As duas listas e a versão de preços. Não vai a mais ninguém: o que um piloto
 * traz no porão e o que ele tem na coleção são dele.
 */
export const openWorldMerchantViewSchema = z
  .object({
    t: z.literal('mv'),
    /** A carga de campo desta nave. Ouro perigoso. */
    f: z.array(openWorldSaleLineSchema).max(OPEN_WORLD_SALE_MAX_LINES),
    /** A coleção segura desta conta, por vender. Ouro seguro. */
    v: z.array(openWorldSaleLineSchema).max(OPEN_WORLD_SALE_MAX_LINES),
    /** Há mais capturas seguras do que as que couberam na lista. */
    more: z.boolean(),
    pv: z.number().int().min(1),
  })
  .strict();

export type OpenWorldMerchantView = z.infer<typeof openWorldMerchantViewSchema>;

/**
 * **O recibo.** Uma resposta por ordem, e ela repete-se sem vender outra vez.
 *
 * Diz quantos, quanto, **para onde**, e sob que tabela de preços — as quatro
 * coisas que um jogador precisa para saber o que aconteceu ao que era dele.
 */
export const openWorldSaleReceiptSchema = z
  .object({
    t: z.literal('mr'),
    id: openWorldCommandIdSchema,
    sc: z.enum(OPEN_WORLD_SALE_SCOPES),
    /** Quantas linhas foram vendidas. */
    n: z.number().int().min(0),
    /** O total liquidado, em Ouro, como decimal. O preço REALMENTE pago. */
    g: z.string().min(1).max(40),
    d: z.enum(OPEN_WORLD_SALE_DESTINATIONS),
    pv: z.number().int().min(1),
  })
  .strict();

export type OpenWorldSaleReceipt = z.infer<typeof openWorldSaleReceiptSchema>;

/** Uma ordem de venda recusada, ao vendedor e com o id que ele enviou. */
export const openWorldSaleRefusalSchema = z
  .object({
    t: z.literal('mf'),
    id: openWorldCommandIdSchema,
    r: z.enum(OPEN_WORLD_SALE_REFUSALS),
  })
  .strict();

export type OpenWorldSaleRefusalFrame = z.infer<typeof openWorldSaleRefusalSchema>;

/**
 * **O comerciante na vista**, público.
 *
 * Posição e alcance: um marco fixo que qualquer um vê e para o qual qualquer um
 * pode voar. O que é privado é o que se negoceia com ele, e isso viaja noutras
 * tramas.
 */
export const openWorldMerchantPresenceSchema = z
  .object({
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    r: z.number().int().min(1),
  })
  .strict();

export type OpenWorldMerchantPresence = z.infer<typeof openWorldMerchantPresenceSchema>;

/* -------------------------------------------------------------------------- */
/*  A identidade da liquidação                                                */
/* -------------------------------------------------------------------------- */

const SEPARATOR = ':';

/**
 * **A chave de idempotência de uma venda**, derivada da ordem.
 *
 * Ela nomeia o âmbito e o comando, e mais nada. Repetir a mesma ordem encontra a
 * operação que já existe e devolve o mesmo recibo — não vende outra vez, não
 * duplica Ouro, e não apaga um segundo peixe.
 *
 * O formato respeita o que a economia exige de uma chave: começa por um
 * alfanumérico e usa apenas caracteres permitidos.
 */
export function openWorldSaleOperationKey(input: {
  readonly scope: OpenWorldSaleScope;
  readonly commandId: string;
}): string {
  return ['ow-sell', input.scope, input.commandId].join(SEPARATOR);
}

/** Um item vendido, como a autoridade o devolve. */
export interface OpenWorldSoldLine {
  readonly id: string;
  readonly speciesId: string;
  readonly rarity: Rarity;
  readonly weightMg: number;
  readonly gold: bigint;
}
